import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Glasses, Package, Cable, BookOpen, Feather, Puzzle,
  ChevronLeft, ChevronRight,
  RotateCcw, Eye, Truck, RefreshCw, Check, ArrowUp,
} from 'lucide-react';
import { GlassPage } from '../components/chrome';
import {
  Accordion, CenteredCard, Eyebrow, FeatureGrid, SectionHeading,
} from '../components/ui';
import { FEATURES, FEATURES_NOTE } from '../lib/content';
import { CONTACT_EMAIL, REFUND_WINDOW, SHIP_WINDOW } from '../lib/legal';

const PRICE = 500;
const PRESCRIPTION_UPCHARGE = 100;

// Lens choice drives both the total and which hosted Stripe link we send the
// buyer to. Payment links carry a fixed amount, so each price has its own link
// and the two must stay in sync with the `price` beside them.
const LENS_OPTIONS = [
  {
    id: 'standard',
    label: 'Non-prescription',
    desc: 'Clear lenses, no correction',
    price: PRICE,
    checkoutUrl: 'https://buy.stripe.com/cNi5kC6YI4Ml4BHgLne3e07',
  },
  {
    id: 'prescription',
    label: 'Prescription',
    desc: 'Made to your script',
    price: PRICE + PRESCRIPTION_UPCHARGE,
    checkoutUrl: 'https://buy.stripe.com/28E6oG5UE2Ed3xDgLne3e08',
  },
];

// Product gallery. Files live in public/photos/, square, with a 640px copy of
// each for phones. Swap the images there and keep the filenames.
const PHOTOS = [
  { src: '/photos/reserve-front.jpg', alt: 'Product render of Korsa Glasses, front view on a model' },
  { src: '/photos/reserve-angle.jpg', alt: 'Product render of Korsa Glasses, three-quarter view on a model' },
  { src: '/photos/reserve-product.jpg', alt: 'Product render of Korsa Glasses on their own' },
];

// Short reassurance row under the buy button. This is the one place the
// refund, prescription, delivery and update promises are spelled out.
const ASSURANCES = [
  { icon: RotateCcw, title: 'Fully refundable', desc: `Cancel any time before your glasses ship for a full refund, issued within ${REFUND_WINDOW}.` },
  { icon: Eye, title: 'Prescription ready', desc: `Add prescription lenses for $${PRESCRIPTION_UPCHARGE}. We collect your script before shipping.` },
  { icon: Truck, title: `Estimated ${SHIP_WINDOW}`, desc: 'An estimate, not a guaranteed date. US deliveries first, in the order pre-orders were placed.' },
  { icon: RefreshCw, title: 'Improves over time', desc: 'KorsaOS updates arrive over the air, so your pair keeps gaining abilities.' },
];

const BOX_ITEMS = [
  { icon: Glasses, label: 'Korsa Glasses' },
  { icon: Package, label: 'Charging case' },
  { icon: Cable, label: 'USB-C charging cable' },
  { icon: Feather, label: 'Microfiber cleaning cloth' },
  { icon: Puzzle, label: 'Replacement pieces' },
  { icon: BookOpen, label: 'Quick start guide' },
];

// Only facts we can stand behind today. Hardware numbers (battery, weight,
// display) go here once they are final.
const DETAILS = [
  ['Price', `$${PRICE}, or $${PRICE + PRESCRIPTION_UPCHARGE} with prescription lenses. Full price paid today in USD, nothing more due later. Tax, if any, shown at checkout.`],
  ['Availability', `Estimated ${SHIP_WINDOW}, US first`],
  ['Lenses', 'Non-prescription or made to your script'],
  ['Software', 'KorsaOS, over-the-air updates, third-party abilities'],
  ['Support', CONTACT_EMAIL],
];

const FAQS = [
  { q: 'What do I pay, and when?', a: `$${PRICE} for non-prescription, or $${PRICE + PRESCRIPTION_UPCHARGE} with prescription lenses. You pay the full price today in USD and nothing more is due when your glasses ship. If the price for your order would ever increase, we tell you first and you can cancel for a full refund.` },
  { q: 'Can I cancel and get my money back?', a: `Yes, any time before your pair ships. Email ${CONTACT_EMAIL} and we refund the full amount to your original payment method within ${REFUND_WINDOW}, no reason needed.` },
  { q: 'When do the glasses ship?', a: `Our estimate is ${SHIP_WINDOW}. That is an estimate, not a guaranteed date. Pre-orders ship in the order they were placed. If the window slips, we email you and you can cancel for a full refund.` },
  { q: 'Can I use my own prescription?', a: `Choose the prescription option above and it adds $${PRESCRIPTION_UPCHARGE} to your order. Once your pair is ready to ship we email you to collect your prescription details.` },
  { q: 'Is there a subscription?', a: 'Not today. Captions, translation, and other AI features are included with your purchase. They run on cloud services that cost us money every time you use them, so we may charge a subscription for them in the future. You would get at least 30 days notice by email, and we would never sign you up automatically. See section 8 of our Terms of Service for details.' },
  { q: 'What exactly am I buying?', a: 'Korsa Glasses, manufactured for Korsa Inc. with KorsaOS built in. This is a pre-order for a product still in development, so features and specifications may change before shipping. Korsa Glasses are not a medical device or hearing aid.' },
];

// --- Gallery ---

// A real swipe track rather than hover-only arrows: phones scroll it with a
// thumb, desktops get the arrows and the thumbnails underneath.
const Gallery = ({ className = '' }) => {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const goTo = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (i + PHOTOS.length) % PHOTOS.length;
    track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
  }, []);

  // The track is the source of truth: swiping and the controls both land here.
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    setIdx(Math.round(track.scrollLeft / track.clientWidth));
  };

  return (
    <div className={className}>
      <div className="group relative">
        <div
          ref={trackRef}
          onScroll={handleScroll}
          // Scroll areas must take keyboard focus, or arrow keys can't reach them (WCAG 2.1.1).
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          role="group"
          aria-label="Product renders. Use the left and right arrow keys to scroll."
          className="glass no-scrollbar flex overflow-x-auto snap-x snap-mandatory overscroll-x-contain"
        >
          {PHOTOS.map((p, i) => (
            <img
              key={p.src}
              src={p.src}
              srcSet={`${p.src.replace('.jpg', '-640.jpg')} 640w, ${p.src} 1024w`}
              sizes="(min-width: 1024px) 55vw, 100vw"
              alt={p.alt}
              draggable="false"
              loading={i === 0 ? 'eager' : 'lazy'}
              className="w-full shrink-0 snap-center aspect-square object-cover"
            />
          ))}
        </div>

        {[
          { dir: -1, Icon: ChevronLeft, label: 'Previous photo', pos: 'left-3' },
          { dir: 1, Icon: ChevronRight, label: 'Next photo', pos: 'right-3' },
        ].map(({ dir, Icon, label, pos }) => (
          <button
            key={label}
            type="button"
            onClick={() => goTo(idx + dir)}
            aria-label={label}
            className={`hidden md:flex absolute ${pos} top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur-md border border-white shadow-[0_4px_16px_rgba(31,38,135,0.15)] items-center justify-center text-zinc-800 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-white transition-all duration-200`}
          >
            <Icon size={20} strokeWidth={2.2} />
          </button>
        ))}
      </div>

      <p className="mt-2 text-xs text-zinc-600 text-center">Product renders. The final product may differ.</p>

      {/* Dots on phones, thumbnails once there is room for them */}
      <div className="mt-4 flex justify-center gap-2 sm:hidden">
        {PHOTOS.map((p, i) => (
          <button
            key={p.src}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Show photo ${i + 1} of ${PHOTOS.length}`}
            aria-pressed={i === idx}
            className="p-2"
          >
            <span
              className={`block h-1.5 rounded-full transition-all duration-300 ${
                i === idx ? 'w-6 bg-zinc-900' : 'w-1.5 bg-zinc-900/25'
              }`}
            />
          </button>
        ))}
      </div>

      <div className="mt-3 hidden sm:grid grid-cols-3 gap-3">
        {PHOTOS.map((p, i) => (
          <button
            key={p.src}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Show ${p.alt}`}
            aria-pressed={i === idx}
            className={`aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
              i === idx ? 'border-zinc-900' : 'border-transparent opacity-60 hover:opacity-100'
            }`}
          >
            <img src={p.src.replace('.jpg', '-640.jpg')} alt="" loading="lazy" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

// --- Page ---

const Order = () => {
  const [redirecting, setRedirecting] = useState(false);
  const [lensId, setLensId] = useState(LENS_OPTIONS[0].id);
  const [showBar, setShowBar] = useState(false);
  const buyButtonRef = useRef(null);
  const lens = LENS_OPTIONS.find((o) => o.id === lensId);
  const total = lens.price;
  const needsPrescription = lensId === 'prescription';

  // The floating bar is a shortcut back to the top, so it stays out of the way
  // on two counts: while the real pre-order button is on screen, and while you
  // are still within the top tenth of the page. It appears once both are false.
  useEffect(() => {
    const el = buyButtonRef.current;
    let buttonOnScreen = true;
    let pastTopTenth = false;
    const update = () => setShowBar(!buttonOnScreen && pastTopTenth);

    const io = el
      ? new IntersectionObserver(([entry]) => {
          buttonOnScreen = entry.isIntersecting;
          update();
        })
      : null;
    if (io) io.observe(el);

    // Lenis scrolls the window itself, so the native scroll event is accurate.
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      pastTopTenth = max > 0 && window.scrollY > max * 0.1;
      update();
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (io) io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Lenis owns the scroll position, so drive in-page links through the shared
  // instance; fall back to native smooth scroll.
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: 'smooth' });
    // Keyboard and screen reader users land where the mouse scrolled to.
    document.getElementById('buy')?.focus({ preventScroll: true });
  };

  // Send buyers to the hosted Stripe link that matches their lens choice.
  const startCheckout = () => {
    if (redirecting) return;
    setRedirecting(true);
    // The terms and marketing checkboxes live on Stripe's checkout page, set in
    // each Payment Link's consent settings. See DEPLOY.md.
    window.location.assign(lens.checkoutUrl);
  };

  return (
    <GlassPage bottomInset>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20 md:pt-28">

        <nav aria-label="Breadcrumb" className="mb-4 text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
          <Link to="/" className="hover:text-zinc-900 transition-colors">Korsa</Link>
          <span className="mx-2" aria-hidden="true">/</span>
          <span className="text-zinc-900" aria-current="page">Glasses</span>
        </nav>

        {/* Hero. On a phone this reads title, price, photos, then the choice;
            on a desktop the right column carries all three beside the gallery. */}
        <section className="flex flex-col gap-5 lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:gap-10 lg:items-start">

          {/* `contents` lets the parts of the right column interleave with the
              gallery on phones, then reassemble as one sticky rail. On a phone
              the run is title, photos, price, buy box; on a desktop the whole
              rail sits beside the gallery in DOM order. */}
          <div className="contents lg:block lg:order-2 lg:sticky lg:top-24">
            <header className="order-1">
              <Eyebrow>Pre-order · Early access</Eyebrow>
              <h1 className="mt-3 text-[2.75rem] sm:text-5xl font-bold tracking-tighter uppercase leading-[0.95] text-zinc-900">
                Korsa Glasses
              </h1>
            </header>

            <div className="order-3 lg:mt-4">
              <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                <span className="text-[2.5rem] sm:text-5xl font-bold tracking-tighter text-zinc-900 leading-none">
                  ${total}
                </span>
              </div>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                Paid today, fully refundable, and nothing more is due when your glasses ship.
              </p>
            </div>

            <div id="buy" tabIndex={-1} className="order-4 glass p-5 sm:p-8 lg:mt-7 scroll-mt-24">
              <div className="flex items-baseline justify-between mb-3">
                <Eyebrow>Lenses</Eyebrow>
                <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
                  {lens.label}
                </span>
              </div>

              <fieldset className="grid gap-2.5 sm:grid-cols-2">
                <legend className="sr-only">Lens type</legend>
                {LENS_OPTIONS.map((opt) => {
                  const selected = opt.id === lensId;
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3.5 text-left cursor-pointer transition-colors duration-200 sm:block has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-zinc-900 ${
                        selected ? 'border-zinc-900 bg-white' : 'border-zinc-500 bg-white/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="lens"
                        value={opt.id}
                        checked={selected}
                        onChange={() => setLensId(opt.id)}
                        className="sr-only"
                      />
                      <span className="block min-w-0">
                        <span className="block text-sm font-bold text-zinc-900 leading-tight">
                          {selected && <Check size={14} strokeWidth={3} className="inline mr-1 -mt-0.5" aria-hidden="true" />}
                          {opt.label}
                        </span>
                        <span className="block text-[11px] text-zinc-600 mt-0.5 leading-snug">{opt.desc}</span>
                      </span>
                      <span className="shrink-0 text-sm font-bold text-zinc-900 sm:block sm:mt-2">
                        {opt.price === PRICE ? `$${opt.price}` : `+$${opt.price - PRICE}`}
                      </span>
                    </label>
                  );
                })}
              </fieldset>

              {/* Announces the new total when the lens changes; the button shows it visually */}
              <p role="status" className="sr-only">
                {lens.label} lenses, ${total} USD today
              </p>

              <div ref={buyButtonRef} className="mt-6">
                <button
                  type="button"
                  onClick={startCheckout}
                  disabled={redirecting}
                  className={`btn-dark w-full py-4 text-xs ${redirecting ? 'opacity-60 cursor-wait' : ''}`}
                >
                  {redirecting ? 'Opening checkout...' : `Pre-order · $${total} USD`}
                </button>
                {/* The full summary sits beside Stripe's Pay button (Payment Link
                    custom text, see DEPLOY.md), so only a pointer is needed here. */}
                <p className="mt-3 text-center text-xs text-zinc-600">
                  <Link to="/terms#pre-orders" className="underline underline-offset-2">Pre-order details</Link>
                </p>

                {/* Picking prescription lenses raises an obvious question at the
                    worst possible moment, right as someone is about to pay. The
                    note expands under the button to answer it before they ask:
                    nothing is needed today. Animated on grid rows so it grows to
                    whatever the copy needs without a hardcoded height. */}
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    needsPrescription ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
                  }`}
                  aria-hidden={!needsPrescription}
                >
                  <div className="overflow-hidden">
                    <p className="flex items-start gap-2.5 rounded-2xl border border-zinc-900/10 bg-white/60 px-4 py-3 text-left text-[11px] leading-relaxed text-zinc-600">
                      <Eye size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-zinc-900" />
                      <span>
                        We'll contact you for your prescription details before your glasses
                        ship. Nothing is needed today.
                      </span>
                    </p>
                  </div>
                </div>

                <p className="mt-3 text-center text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  Secure checkout by Stripe
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-zinc-900/5 flex flex-wrap gap-x-5 gap-y-2">
                {[
                  ['korsaos', 'What it does'],
                  ['details', 'Details'],
                  ['faq', 'FAQ'],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => scrollTo(id)}
                    className="text-[10px] uppercase tracking-widest font-bold text-zinc-600 hover:text-zinc-900 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <Gallery className="order-2 lg:order-1" />
        </section>

        {/* Assurances: one card of rows on a phone, four cards once wide */}
        <section className="mt-5 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
          <div className="glass p-1.5 sm:contents">
            {ASSURANCES.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.title} className="flex gap-4 p-4 sm:glass sm:block sm:p-6">
                  <Icon size={20} strokeWidth={2} className="shrink-0 mt-0.5 text-zinc-600 sm:mb-3" />
                  <div>
                    <h2 className="text-[15px] sm:text-base font-bold tracking-tight text-zinc-900 mb-1">{a.title}</h2>
                    <p className="text-sm text-zinc-600 leading-relaxed">{a.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <section id="korsaos" className="section scroll-mt-20">
        <div className="panel max-w-[1400px] mx-auto">
          <SectionHeading
            eyebrow="KorsaOS"
            title="What it does"
            lead="Eight abilities that run on the lens, built for the moments where words get hard. They stay out of the way until you need them."
          />
          <p className="mt-4 text-sm text-zinc-600">{FEATURES_NOTE}</p>
          <FeatureGrid items={FEATURES} swipeOnMobile className="mt-8 md:mt-14" />
        </div>
      </section>

      <section id="details" className="section scroll-mt-20">
        <div className="panel max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">

            <div>
              <SectionHeading eyebrow="In the box" title="What's included" />
              <div className="mt-6 md:mt-8 grid gap-2.5 sm:grid-cols-2 md:gap-4">
                {BOX_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="card flex items-center gap-3 px-4 py-3.5 sm:px-5 sm:py-4">
                      <Icon size={18} strokeWidth={2} className="text-zinc-600 shrink-0" />
                      <span className="text-sm md:text-base font-bold text-zinc-900">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:border-l lg:border-zinc-900/5 lg:pl-16">
              <SectionHeading eyebrow="Details" title="More info" />
              <dl className="mt-6 md:mt-8 divide-y divide-zinc-900/5">
                {DETAILS.map(([label, value]) => (
                  <div key={label} className="py-3.5 sm:grid sm:grid-cols-[7.5rem_1fr] sm:gap-4 sm:items-baseline">
                    <dt className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1 sm:mb-0">{label}</dt>
                    <dd className="text-sm text-zinc-700 leading-relaxed">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 text-sm text-zinc-500 leading-relaxed">
                Questions about your prescription? Email{' '}
                <a href="mailto:support@korsa.tech" className="text-zinc-900 underline underline-offset-2">support@korsa.tech</a>.
              </p>
            </div>

          </div>
        </div>
      </section>

      <section id="faq" className="section scroll-mt-20">
        <div className="panel max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-16">
            <SectionHeading
              eyebrow="Support"
              title="Questions"
              lead="The money, the timeline, and what you are actually buying."
            />
            <Accordion items={FAQS} />
          </div>
        </div>
      </section>

      {/* Floating buy bar. Hidden while the product section is on screen and
          shown at every width once it has scrolled away. Tapping it returns you
          to the top rather than paying straight away, so the lens choice is
          confirmed before checkout. */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 px-4 pt-3 pb-safe transition-all duration-300 ${
          showBar ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 invisible'
        }`}
      >
        <button
          type="button"
          onClick={scrollToTop}
          aria-label={`Back to top to pre-order, $${total}, ${lens.label}`}
          className="glass-pill w-full sm:max-w-md sm:mx-auto flex items-center gap-3 pl-5 pr-2 py-2 text-left"
        >
          <span className="min-w-0">
            <span className="block text-base font-bold tracking-tight text-zinc-900 leading-none">${total}</span>
            <span className="block text-[10px] uppercase tracking-widest font-bold text-zinc-500 mt-1 truncate">
              {lens.label}
            </span>
          </span>
          <span className="btn-dark ml-auto px-6 py-3">
            <ArrowUp size={14} strokeWidth={2.5} />
            Pre-order
          </span>
        </button>
      </div>
    </GlassPage>
  );
};

export const OrderThanks = () => (
  <CenteredCard>
    <div className="mx-auto w-16 h-16 rounded-full border border-green-500/30 bg-green-500/10 flex items-center justify-center text-green-600 mb-8">
      <Check size={28} strokeWidth={2.5} />
    </div>
    <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-zinc-900 mb-4">Pre-order confirmed</h1>
    <p className="text-sm md:text-base text-zinc-600 leading-relaxed mb-4">
      Your pair is reserved. Your payment stays fully refundable until your glasses
      ship: email <a href={`mailto:${CONTACT_EMAIL}`} className="text-zinc-900 underline underline-offset-2">{CONTACT_EMAIL}</a> anytime
      and we'll refund you within {REFUND_WINDOW}, no reason needed.
    </p>
    <p className="text-sm md:text-base text-zinc-600 leading-relaxed mb-4">
      Our estimated ship window is {SHIP_WINDOW}. That's an estimate, and if it slips
      we'll email you and you can cancel for a full refund. If you chose prescription
      lenses we'll collect your script before your pair ships. Nothing more is due.
    </p>
    <p className="text-sm text-zinc-600 leading-relaxed mb-10">
      Read the <Link to="/terms#pre-orders" className="text-zinc-900 underline underline-offset-2">pre-order terms</Link>.
    </p>
    <Link to="/" className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 transition-colors">
      Back to home →
    </Link>
  </CenteredCard>
);

export default Order;
